/* eslint-disable indent*/
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { format } from 'date-fns';

import { Modal } from '@/common';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import {
  AUDIO,
  COVER_PHOTO,
  IMAGE,
  IMAGES_VIDEOS,
  PLAYLIST,
  TEXT,
  TRIBUTE_VIDEO,
  VIDEO
} from '@/constants';
import { AuthContext } from '@/contexts';
import { CustomAttributeType, PERMISSION_TYPE, TagType } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getPermissionsForOrganization, getUploadRequestObject, groupBy } from '@/helpers';
import { formatDate } from '@/helpers/date';
import { showToast, ToastType } from '@/helpers/toast';
import { UploadContentLayout } from '@/layout';
import { FolderItem } from '@/pages/Event/UploadContent/components/FolderItem';
import { UploadRequest } from '@/pages/Event/UploadContent/components/UploadRequest';
import { PlusIcons } from '@/resources';
import {
  useConfirmedContentUploadMutation,
  useCreateTagMutation,
  useGetCustomAttributesByEventType,
  useGetEventAssets,
  useGetEventByIdQuery,
  useGetEventTags,
  useGetEventTributeVideos,
  useGetOrganizationByIdSimplifiedQuery
} from '@/services';
import { Asset, AssetSlot, CustomAttribute, Tag } from '@/types';

const REQUEST_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
};

const REQUEST_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit'
};
const INTRO_TEXT_MAX_WIDTH = 'max-w-[710px]';

export const UploadContentRequests = () => {
  const { eventId } = useParams();
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { permissions, organizationPermissions: userOrganizationPermissions } =
    useContext(AuthContext);
  const [isButtonDisabledAfterClick, setButtonDisabledAfterClick] = useState(false);
  const [isConfirmUploadModalOpen, setIsConfirmUploadModalOpen] = useState(false);
  const [newFolderId, setNewFolderId] = useState<number | null>(null);
  const [selectionFolderId, setSelectionFolderId] = useState<number | null>(null);

  const { data: eventData, isLoading: isEventLoading } = useGetEventByIdQuery(Number(eventId));
  const { data: eventOrganization } = useGetOrganizationByIdSimplifiedQuery(
    Number(eventData?.data?.organization?.id)
  );
  // An uploader can belong to several organizations, so the ceremony's organization must have
  // the permission too, not just one of the uploader's organizations.
  const organizationPermissions = useMemo(
    () => getPermissionsForOrganization(userOrganizationPermissions, eventOrganization?.data),
    [eventOrganization, userOrganizationPermissions]
  );

  const hasFamilyPortalAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL
  );
  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );
  const hasTributeVideoCreation = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION
  );
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const { data: customAttributes, isLoading: isLoadingCustomAttributes } =
    useGetCustomAttributesByEventType(Number(eventId), CustomAttributeType.CREATE_EDIT);

  const ceremonyPersonName = useMemo(() => {
    const getValue = (label: string) =>
      customAttributes?.data?.find((ca: CustomAttribute) => ca.label === label)?.value?.trim();

    const coupleName = [getValue('groomName'), getValue('brideName')].filter(Boolean).join(' & ');

    return (
      getValue('nameOfDeceased') || coupleName || getValue('celebrantName') || eventData?.data?.name
    );
  }, [customAttributes, eventData]);

  const ceremonyTypeKey = useMemo(() => {
    const labels = new Set(customAttributes?.data?.map((ca: CustomAttribute) => ca.label));
    if (labels.has('nameOfDeceased')) {
      return 'ceremonyTypeFuneral';
    }
    if (labels.has('groomName') || labels.has('brideName')) {
      return 'ceremonyTypeWedding';
    }
    if (labels.has('celebrantName')) {
      return 'ceremonyTypeBirthday';
    }
    return 'ceremonyTypeDefault';
  }, [customAttributes]);

  const { isLoading: isLoadingTags, data: allTags } = useGetEventTags(eventId ?? '');
  const isFreeUploadZoneEnabled = !!eventData?.data?.isFreeUploadZoneEnabled;
  const isContentUploadConfirmed = !!eventData?.data?.isContentUploadConfirmed;

  const { data: allAssets, isLoading: isAssetsLoading } = useGetEventAssets(
    eventId ?? '',
    allTags?.data
      ?.filter((tag: Tag) => [COVER_PHOTO, AUDIO, IMAGE, VIDEO, TEXT].includes(tag.name))
      .map((tag: Tag) => `${tag.id}`),
    0,
    '',
    undefined,
    undefined,
    !isLoadingTags && !!allTags,
    hasContentManagementAccess
  );
  const audioAssets = useMemo(() => {
    return (
      allAssets?.pages
        .flatMap((page) => page.data?.items)
        .filter((asset: Asset) => asset.tags?.some((tag) => tag.name === AUDIO)) || []
    );
  }, [allAssets]);

  const folderTags = allTags?.data ?? [];

  const { data: folderAssets } = useGetEventAssets(
    eventId ?? '',
    folderTags.map((tag: Tag) => `${tag.id}`),
    1,
    undefined,
    undefined,
    undefined,
    true,
    isFreeUploadZoneEnabled && folderTags.length > 0
  );
  const hasFolderAssets = !!folderAssets?.pages[0]?.data?.items?.length;

  const tributeVideoTags = allTags?.data
    ?.filter((tag: Tag) => tag.name === TRIBUTE_VIDEO)
    .map((tag: Tag) => `${tag.id}`);

  // Organization users see every tribute video; others only those tagged for this ceremony, and
  // '-1' matches nothing when there is no such tag.
  const getTributeVideoTagFilter = () => {
    if (hasOrgAccess) {
      return [];
    }
    return tributeVideoTags?.length === 0 ? ['-1'] : tributeVideoTags;
  };

  const { data: allTributeVideos, isLoading: isLoadingTributeVideos } = useGetEventTributeVideos(
    eventId ?? '',
    0,
    getTributeVideoTagFilter(),
    undefined,
    hasTributeVideoCreation
  );

  const { mutate: confirmedContentUpload } = useConfirmedContentUploadMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('contentUploadConfirmed'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: createTag, isLoading: isCreatingFolder } = useCreateTagMutation(queryClient, {
    onSuccess: (response) => {
      showToast(ToastType.SUCCESS, t('folderCreated'));
      setNewFolderId(response?.data?.id ?? null);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: createFolderSilently } = useCreateTagMutation(queryClient, {
    onSuccess: (response) => {
      setNewFolderId(response?.data?.id ?? null);
    },
    onError: () => undefined
  });

  const getDefaultFolderNumber = (name: string) => {
    const [prefix, suffix] = t('defaultFolderName', { number: '\u0000' }).split('\u0000');
    if (!name.startsWith(prefix) || !name.endsWith(suffix)) {
      return null;
    }
    const number = name.slice(prefix.length, name.length - suffix.length);
    return /^\d+$/.test(number) ? Number(number) : null;
  };

  const getDefaultFolderName = () => {
    const takenNames = new Set(folderTags.map((tag: Tag) => tag.name.toLowerCase()));
    const highestNumber = Math.max(
      0,
      ...folderTags.map((tag: Tag) => getDefaultFolderNumber(tag.name) ?? 0)
    );
    let number = highestNumber + 1;
    while (takenNames.has(t('defaultFolderName', { number }).toLowerCase())) {
      number++;
    }
    return t('defaultFolderName', { number });
  };

  const handleCreateFolder = () => {
    createTag({ eventId: Number(eventId), name: getDefaultFolderName(), type: TagType.ASSET_TAG });
  };

  const hasRequestedFirstFolderRef = useRef(false);
  const shouldCreateFirstFolder =
    isFreeUploadZoneEnabled &&
    !!allTags &&
    folderTags.length === 0 &&
    !eventData?.data?.isContentUploadConfirmed;

  useEffect(() => {
    hasRequestedFirstFolderRef.current = false;
    setNewFolderId(null);
    setSelectionFolderId(null);
    setButtonDisabledAfterClick(false);
  }, [eventId]);

  useEffect(() => {
    if (!shouldCreateFirstFolder || hasRequestedFirstFolderRef.current) {
      return;
    }
    hasRequestedFirstFolderRef.current = true;
    createFolderSilently({
      eventId: Number(eventId),
      name: t('defaultFolderName', { number: 1 }),
      type: TagType.ASSET_TAG
    });
  }, [shouldCreateFirstFolder, eventId]);

  const prepareAssetsAndSlots = () => {
    const assets =
      allAssets?.pages
        .flatMap((page) => page.data?.items)
        .map((asset: Asset) => ({
          ...asset,
          assetType:
            asset.tags.some((tag) => tag.name === IMAGE || tag.name === VIDEO) &&
            [IMAGE, VIDEO].includes(asset.assetType)
              ? IMAGES_VIDEOS
              : asset.assetType
        })) || [];

    const tributeVideos =
      allTributeVideos?.pages
        .flatMap((page) => page.data?.items)
        .map((tributeVideo: Asset) => ({
          ...tributeVideo,
          assetType: TRIBUTE_VIDEO,
          tags: tributeVideo?.tags?.map((tag) => tag.name === TRIBUTE_VIDEO)
        })) ?? [];

    const allMergedAssets = [...(assets as Asset[]), ...tributeVideos];

    const assetSlots: AssetSlot[] = eventData?.data.assetSlots
      .filter((assetSlot: AssetSlot) => assetSlot.slotType?.assetType !== PLAYLIST)
      .map((assetSlot: AssetSlot) => ({
        ...assetSlot,
        slotType: {
          ...assetSlot.slotType,
          assetType:
            assetSlot.slotType && [IMAGE, VIDEO].includes(assetSlot.slotType.assetType as string)
              ? IMAGES_VIDEOS
              : assetSlot.slotType?.assetType
        }
      }));

    const groupedAssets = groupBy(
      (allMergedAssets as Asset[]) ?? [],
      ({ assetType }: Asset) => assetType
    );

    const groupedAssetSlots = groupBy(assetSlots, ({ slotType }: AssetSlot) => slotType?.assetType);

    return { groupedAssets, groupedAssetSlots };
  };

  const getNumberOfAssets = useCallback(
    (key: string) => {
      const { groupedAssets } = prepareAssetsAndSlots();

      if (key === AUDIO) {
        return audioAssets?.length ?? 0;
      }

      if (key === COVER_PHOTO) {
        const coverPhotoTaggedImages = (groupedAssets.get(IMAGE) ?? []).filter((asset) =>
          asset.tags?.some((t) => t.name === COVER_PHOTO)
        );

        const coverPhotoTaggedImagesCount = coverPhotoTaggedImages.length;

        return (
          (groupedAssets.get(key)?.length ?? 0) +
          (eventData?.data.relatedUsersCanGenerate ? 0 : coverPhotoTaggedImagesCount)
        );
      }

      return groupedAssets.get(key)?.length ?? 0;
    },
    [audioAssets, eventData]
  );

  const getUploadRequests = () => {
    const result = [];
    const { groupedAssetSlots } = prepareAssetsAndSlots();

    for (const [key, value] of groupedAssetSlots) {
      const uploadRequestObject = getUploadRequestObject(key as string);
      result.push(
        <UploadRequest
          key={key}
          eventId={eventId ?? ''}
          uploadRequestObject={uploadRequestObject}
          numberOfAssets={getNumberOfAssets(key as string)}
          numberOfAssetSlots={value.length}
          uploadConfirmed={eventData?.data.isContentUploadConfirmed}
        />
      );
    }

    return result;
  };

  const handleConfirm = () => {
    if (!eventData?.data?.isContentUploadConfirmed) {
      confirmedContentUpload(eventData?.data.id);
    }
    setIsConfirmUploadModalOpen(false);
    setButtonDisabledAfterClick(true);
  };

  // Named in the "feature unavailable" dialog; Family Portal is reported first when both are off.
  const getMissingPermissionName = () => {
    if (!hasFamilyPortalAccess) {
      return t(`${ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL}`);
    }
    if (!hasContentManagementAccess) {
      return t(`${ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT}`);
    }
    return '';
  };

  const handleDisable = () => {
    if (isFreeUploadZoneEnabled) {
      return !!eventData?.data?.isContentUploadConfirmed || !hasFolderAssets;
    }
    const { groupedAssets, groupedAssetSlots } = prepareAssetsAndSlots();

    if (groupedAssets.size === 0) {
      return true;
    }

    for (const [key, value] of groupedAssetSlots) {
      if ((groupedAssets.get(key as string)?.length ?? 0) < value.length) {
        return true;
      } else if ((groupedAssets.get(key as string)?.length ?? 0) > value.length) {
        return true;
      }
    }
    return false;
  };

  return (
    <UploadContentLayout>
      {(isEventLoading ||
        (hasContentManagementAccess && isAssetsLoading) ||
        isLoadingTags ||
        isLoadingCustomAttributes ||
        (hasTributeVideoCreation && isLoadingTributeVideos)) && (
        <div className='flex h-screen items-center justify-center'>
          <Spinner />
        </div>
      )}
      {!isEventLoading &&
        (hasContentManagementAccess ? !isAssetsLoading : true) &&
        !isLoadingTags &&
        !isLoadingCustomAttributes &&
        (hasTributeVideoCreation ? !isLoadingTributeVideos : true) &&
        eventData &&
        (hasContentManagementAccess ? allAssets : true) &&
        allTags && (
          <div
            className={`flex size-full flex-row items-center justify-center ${hasFamilyPortalAccess || hasContentManagementAccess ? '' : 'relative'}`}>
            <div className='flex w-full max-w-[1476px] flex-col justify-between md:px-[18px]'>
              <div className='mb-4 flex w-full flex-row items-center justify-between gap-4 pt-[14px]'>
                <Label text={t('uploadRequest')} type={LABEL_TYPE.H2} className='!text-[36px]' />
                <Button
                  disabled={handleDisable() || isButtonDisabledAfterClick}
                  handleOnClick={() => setIsConfirmUploadModalOpen(true)}
                  variant={ButtonVariant.PRIMARY}
                  className='w-[150px] shrink-0 !px-2 !text-base'>
                  {eventData?.data?.isContentUploadConfirmed ? t('confirmed') : t('submitChanges')}
                </Button>
              </div>
              {isContentUploadConfirmed && (
                <Label
                  type={LABEL_TYPE.PARAGRAPH2}
                  className='mr-2 pb-4 font-sans text-accent-dark-gray'
                  text={t('contentUploadedMessage', {
                    name: ceremonyPersonName,
                    date: format(new Date(eventData.data.startDateAndTime), 'dd/MM/yyyy'),
                    time: format(new Date(eventData.data.startDateAndTime), 'HH:mm')
                  })}
                />
              )}
              {isFreeUploadZoneEnabled && !isContentUploadConfirmed && (
                <div>
                  <p
                    className={`mr-2 pb-2 font-sans text-sm text-accent-dark-gray ${INTRO_TEXT_MAX_WIDTH}`}>
                    <Trans
                      t={t}
                      i18nKey='uploadRequestDeadlineMessage'
                      values={{
                        ceremonyType: t(ceremonyTypeKey),
                        name: ceremonyPersonName,
                        date: formatDate(
                          new Date(eventData.data.contentDeadline),
                          REQUEST_DATE_FORMAT_OPTIONS
                        ),
                        time: formatDate(
                          new Date(eventData.data.contentDeadline),
                          REQUEST_TIME_FORMAT_OPTIONS
                        )
                      }}
                      components={{ b: <span className='font-semibold text-primary-blue' /> }}
                    />
                  </p>
                  <Label
                    type={LABEL_TYPE.PARAGRAPH2}
                    className={`mr-2 pb-2 font-sans text-accent-dark-gray ${INTRO_TEXT_MAX_WIDTH}`}
                    text={t('uploadRequestFoldersMessage')}
                  />
                  <Label
                    type={LABEL_TYPE.PARAGRAPH2}
                    className={`mr-2 pb-4 font-sans text-accent-dark-gray ${INTRO_TEXT_MAX_WIDTH}`}
                    text={t('uploadRequestContinueLaterMessage')}
                  />
                  {eventData?.data?.streamEvent?.enabled && (
                    <div
                      className='mb-[10px] flex w-full cursor-pointer flex-row items-center justify-between gap-2.5 rounded-[5px] bg-secondary-light-gray p-2.5'
                      onClick={() => navigate(`/events/${eventId}/upload-content/live-stream`)}>
                      <Label
                        text={t('liveStream')}
                        className='h-[41px] pl-[5px] text-xl font-semibold !text-primary-blue'
                      />
                    </div>
                  )}
                  {folderTags.map((folder: Tag, index: number) => (
                    <FolderItem
                      key={folder.id}
                      eventId={eventId ?? ''}
                      organizationId={eventData?.data?.organization?.id}
                      folder={folder}
                      isExpandedByDefault={index === 0 || folder.id === newFolderId}
                      hint={
                        getDefaultFolderNumber(folder.name) === null
                          ? undefined
                          : t('defaultFolderHint')
                      }
                      organizationPermissions={organizationPermissions}
                      isSelectionActive={folder.id === selectionFolderId}
                      onSelectionStart={setSelectionFolderId}
                    />
                  ))}
                  <div className='flex w-full'>
                    <Button
                      className='mb-3 mt-1 !h-[50px] w-full !font-medium'
                      variant={ButtonVariant.QUINARY}
                      disabled={isCreatingFolder}
                      handleOnClick={handleCreateFolder}>
                      <PlusIcons className='size-3.5 md:mr-3' fill='var(--color-primary-blue)' />
                      {t('createNewFolder')}
                    </Button>
                  </div>
                </div>
              )}
              {!isFreeUploadZoneEnabled && (
                <>
                  {!isContentUploadConfirmed && (
                    <div>
                      <Label
                        type={LABEL_TYPE.PARAGRAPH2}
                        className='mr-2 pb-2 font-sans text-accent-dark-gray'
                        text={t('uploaderIntro', {
                          name: ceremonyPersonName,
                          date: formatDate(
                            new Date(eventData.data.contentDeadline),
                            REQUEST_DATE_FORMAT_OPTIONS
                          ),
                          time: formatDate(
                            new Date(eventData.data.contentDeadline),
                            REQUEST_TIME_FORMAT_OPTIONS
                          )
                        })}
                      />
                      <Label
                        type={LABEL_TYPE.PARAGRAPH2}
                        className='mr-2 pb-4 font-sans text-accent-dark-gray'
                        text={t('uploaderIntroSecondary')}
                      />
                    </div>
                  )}
                  {getUploadRequests()}
                  {eventData?.data?.streamEvent?.enabled && (
                    <div
                      className='mb-[10px] flex w-full cursor-pointer flex-row items-center justify-between gap-2.5 rounded-[5px] bg-secondary-light-gray p-2.5'
                      onClick={() => navigate(`/events/${eventId}/upload-content/live-stream`)}>
                      <Label
                        text={t('liveStream')}
                        className='h-[41px] pl-[5px] text-xl font-semibold !text-primary-blue'
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            {(!hasFamilyPortalAccess || !hasContentManagementAccess) && (
              <div className='absolute inset-0 z-10 rounded-2xl backdrop-blur-sm'>
                <Modal
                  isOpen={!hasFamilyPortalAccess || !hasContentManagementAccess}
                  modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
                  hasCloseIcon={false}
                  onClose={() => {
                    undefined;
                  }}>
                  <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
                    <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
                    <Label
                      type={LABEL_TYPE.PARAGRAPH2}
                      text={t('yourOranizationDoesntHavePermission', {
                        permission: getMissingPermissionName()
                      })}
                    />
                  </div>
                </Modal>
              </div>
            )}
            {isConfirmUploadModalOpen && (
              <ConfirmationModal
                isOpen={isConfirmUploadModalOpen}
                message={t('confirmContentUploadMessage')}
                showFullMessage
                onClose={() => setIsConfirmUploadModalOpen(false)}
                onConfirm={handleConfirm}
              />
            )}
          </div>
        )}
    </UploadContentLayout>
  );
};
